import DataNumberIcon from '@atlaskit/icon/core/data-number';
import FieldDropdownIcon from '@atlaskit/icon/core/field-dropdown';
import PersonIcon from '@atlaskit/icon/core/person';
import TeamsIcon from '@atlaskit/icon/core/teams';
import TextShortenIcon from '@atlaskit/icon/core/text-shorten';
import { token } from '@atlaskit/tokens';
import React from 'react';
import type { IntlShape } from 'react-intl';

import type { CustomFieldEmptyState } from './types';

export const getEmptyStateFields = (intl: IntlShape): CustomFieldEmptyState[] => [
	{
		type: 'STRING',
		name: intl.formatMessage({
			id: 'townsquare.teams-settings.custom-field-examples.skills-name',
			description: 'Example field name for skills',
			defaultMessage: 'Skills',
		}),
		description: intl.formatMessage({
			id: 'townsquare.teams-settings.custom-field-examples.skills-description',
			description: 'Example field description for skills',
			defaultMessage: 'Teams skillset and expertise',
		}),
	},
	{
		type: 'SELECTOR',
		name: intl.formatMessage({
			id: 'townsquare.teams-settings.custom-field-examples.cost-centre-name',
			description: 'Example field name for cost centre',
			defaultMessage: 'Cost centre',
		}),
		description: intl.formatMessage({
			id: 'townsquare.teams-settings.custom-field-examples.cost-centre-description',
			description: 'Example field description for cost centre',
			defaultMessage: 'Cost centre codes assigned by the organisation',
		}),
	},
	{
		type: 'USER',
		name: intl.formatMessage({
			id: 'townsquare.teams-settings.custom-field-examples.contacts-name',
			description: 'Example field name for contacts',
			defaultMessage: 'Contacts',
		}),
		description: intl.formatMessage({
			id: 'townsquare.teams-settings.custom-field-examples.contacts-description',
			description: 'Example field description for contacts',
			defaultMessage: 'Person who is best placed to represent the team for general inquiries',
		}),
	},
];

const CURRENCY_CODES = ['USD', 'AUD', 'EUR', 'GBP', 'JPY', 'SGD', 'CAD'] as const;

const getCurrencySymbol = (code: string): string => {
	try {
		const parts = new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: code,
			currencyDisplay: 'narrowSymbol',
		}).formatToParts(0);
		return parts.find(p => p.type === 'currency')?.value ?? code;
	} catch {
		return code;
	}
};

export const getCurrencyOptions = () =>
	CURRENCY_CODES.map(code => ({
		label: `${getCurrencySymbol(code)} ${code}`,
		value: code,
	}));

export const DEFAULT_CURRENCY = 'USD';

export const renderCustomFieldIcon = (type: string) => {
	const iconProps = {
		label: '',
		color: token('color.icon'),
	};

	switch (type) {
		case 'STRING':
			return <TextShortenIcon {...iconProps} color={token('color.icon')} />;
		case 'SELECTOR':
			return <FieldDropdownIcon {...iconProps} color={token('color.icon')} />;
		case 'USER':
			return <PersonIcon {...iconProps} color={token('color.icon')} />;
		case 'NUMBER':
			return <DataNumberIcon {...iconProps} color={token('color.icon')} />;
		case 'TEAM':
			return <TeamsIcon {...iconProps} color={token('color.icon')} />;
		default:
			return <TextShortenIcon {...iconProps} color={token('color.icon')} />;
	}
};
